import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router'
import type { CareTeamSwapSearchResponse } from './CareTeamSwapService'
import { roleAbbr } from './roles'
import { LicensureChip, isLicensureIssue, worstLicensure, type LicensureDetail } from './licensure'

type SearchMember = CareTeamSwapSearchResponse['results'][number]
type CareTeamProvider = SearchMember['care_team'][number]

interface ResultsTableProps {
  members: SearchMember[]
  validated: boolean
  // departing provider id -> replacement full name, from the proposed swaps
  replacementByProvider: Map<number, string>
}

const memberName = (member: SearchMember) =>
  [member.first_name, member.last_name].filter(Boolean).join(' ') || `Member ${member.id}`

// The worst status across a team (current or the resulting post-swap team), for its rolled-up cell.
const teamWorst = (team: CareTeamProvider[]) =>
  worstLicensure(team.map(provider => provider.licensure_status))

// The providers behind a rolled-up non-clean status, for the status-chip tooltip.
const teamIssues = (team: CareTeamProvider[]): LicensureDetail[] =>
  uniqueCareTeam(team)
    .filter(provider => isLicensureIssue(provider.licensure_status))
    .map(provider => ({
      label: [roleAbbr(provider.role_name), provider.full_name].filter(Boolean).join(' '),
      status: provider.licensure_status,
    }))

// A member's panel can carry the same provider more than once; show each provider once.
const uniqueCareTeam = (careTeam: CareTeamProvider[]) => {
  const seen = new Set<number>()
  return careTeam.filter(provider => !seen.has(provider.id) && seen.add(provider.id))
}

const HEADER_CELL = {
  bgcolor: 'grey.50',
  fontSize: 12,
  fontWeight: 700,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
}

const CHIP_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  borderRadius: 20,
  px: 1.25,
  py: 0.375,
  fontSize: 12,
  whiteSpace: 'nowrap',
  border: '1px solid',
}

const RoleTag = ({ abbr, color }: { abbr: string; color: string }) => (
  <Box component="span" sx={{ fontWeight: 700, fontSize: 10, letterSpacing: 0.3, color }}>
    {abbr}
  </Box>
)

const CareTeamChips = ({
  member,
  replacementByProvider,
}: {
  member: SearchMember
  replacementByProvider: Map<number, string>
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {uniqueCareTeam(member.care_team).map(provider => {
      const replacement = replacementByProvider.get(provider.id)
      const abbr = roleAbbr(provider.role_name)
      if (replacement) {
        return (
          <Box
            key={provider.id}
            component="span"
            sx={{
              ...CHIP_BASE,
              bgcolor: 'primary.light',
              borderColor: 'primary.light',
              color: 'primary.dark',
            }}
          >
            <RoleTag abbr={abbr} color="primary.main" />
            <Box component="span" sx={{ color: 'text.disabled', textDecoration: 'line-through' }}>
              {provider.full_name}
            </Box>
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
              →
            </Box>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {replacement}
            </Box>
          </Box>
        )
      }
      return (
        <Box
          key={provider.id}
          component="span"
          sx={theme => ({
            ...CHIP_BASE,
            bgcolor: theme.palette.grey[100],
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.secondary,
          })}
        >
          <RoleTag abbr={abbr} color="text.disabled" />
          {provider.full_name}
        </Box>
      )
    })}
  </Box>
)

/**
 * The searched cohort with its current-team tri-state licensure and, once validated, the proposed
 * (new) licensure. The server returns one page of rows (ordered by member id), so the table renders
 * them in that order.
 */
const ResultsTable = ({ members, validated, replacementByProvider }: ResultsTableProps) => {
  return (
    <Box
      sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      data-testid="results-table"
    >
      <Table sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={HEADER_CELL}>Member</TableCell>
            <TableCell sx={HEADER_CELL}>State</TableCell>
            <TableCell sx={HEADER_CELL}>Contract</TableCell>
            <TableCell sx={HEADER_CELL}>
              Care team{' '}
              <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled' }}>
                (current → proposed)
              </Box>
            </TableCell>
            <TableCell sx={HEADER_CELL}>Current licensure</TableCell>
            <TableCell sx={HEADER_CELL}>New licensure</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map(member => {
            const proposedTeam = member.proposed_care_team
            const proposed = validated ? teamWorst(proposedTeam) : null
            const flagged = validated && isLicensureIssue(proposed)
            return (
              <TableRow
                key={member.id}
                hover={!flagged}
                sx={
                  flagged
                    ? {
                        bgcolor: theme => alpha(theme.palette.error.main, 0.06),
                        '&:hover': { bgcolor: theme => alpha(theme.palette.error.main, 0.1) },
                      }
                    : undefined
                }
              >
                <TableCell sx={{ fontWeight: 600 }}>
                  {member.user_id != null ? (
                    <Link
                      component={RouterLink}
                      to={`/patients/${member.user_id}/profile`}
                      target="_blank"
                      rel="noopener"
                    >
                      {memberName(member)}
                    </Link>
                  ) : (
                    memberName(member)
                  )}
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={theme => ({
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 1,
                      px: 1,
                      py: 0.375,
                      fontWeight: 700,
                      fontSize: 12,
                      color: 'text.secondary',
                    })}
                  >
                    {member.state ?? '—'}
                  </Box>
                </TableCell>
                <TableCell>
                  {member.contract ? (
                    <Typography component="span" sx={{ fontSize: 13 }}>
                      {member.contract}
                    </Typography>
                  ) : (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <CareTeamChips member={member} replacementByProvider={replacementByProvider} />
                </TableCell>
                <TableCell>
                  <LicensureChip
                    status={teamWorst(member.care_team)}
                    details={teamIssues(member.care_team)}
                  />
                </TableCell>
                <TableCell>
                  {validated ? (
                    <LicensureChip status={proposed} details={teamIssues(proposedTeam)} />
                  ) : (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default ResultsTable
